import React from 'react';
import { BodyText, Button, Checkbox, Gutter } from '@gaiads/telia-react-component-library';
import { Card, CardProps, DataTable, DataTableProps } from '@cdaas/reusable-components-daas';
import { useTranslation } from 'react-i18next';
import { CountryAcquisitionPrice, AdminProductVariant, Country } from '@cdaas/api-clients/dist/product-service-openapi';
import { useSelector } from 'react-redux';
import { selectFeatureFlags } from '../../../store/featureFlags';
import { Norway } from '../../../utils/countries';

interface ProductPricingCardProps {
  variants: AdminProductVariant[];
  countryId: number;
  countries: Country[];
  onEditClick: () => void;
  onRefreshProductDataClick: () => void;
}

const ProductPricingCard = ({ variants, countryId, countries, onEditClick, onRefreshProductDataClick }: ProductPricingCardProps) => {
  const priceMasterEnabled = useSelector(selectFeatureFlags).FEATURE_FLAG_PRICE_MASTER_ENABLED;
  const { t } = useTranslation('productscreen');

  // For now each variant should have the same acquisition price in same country.
  // If any variant doesn't have an acquisition price in the currently chosen country => interpret it as missing price.
  const acquisitionPricesInSelectedCountry = variants.reduce((acquisitionPrices: CountryAcquisitionPrice[], variant) => {
    const acquisitionPrice = variant.country_acquisition_prices.find(price => price.country_id === countryId);
    if (acquisitionPrice) {
      acquisitionPrices.push(acquisitionPrice);
    }
    return acquisitionPrices;
  }, []);
  const acquisitionPrice = acquisitionPricesInSelectedCountry.find(price => price !== undefined);
  const missingAcquisitionPrice = acquisitionPrice === undefined;
  const country = countries.find(country => country.id === countryId);
  const currency = country?.currency || '';

  const cardTag: CardProps['tag'] = missingAcquisitionPrice ? { text: t('productPricingCard.updateRequired') } : undefined;
  const cardProps: CardProps = {
    isInitiallyOpen: true,
    title: t('productPricingCard.title'),
    tag: cardTag,
    wrapperDivCustomProps: { backgroundColor: 'white' },
  };

  const priceMasterActive = priceMasterEnabled && country?.code === Norway.code;

  const acquisitionPriceLabel =
    priceMasterActive && variants.filter(variant => variant.materials?.some(material => material.country_id === countryId)).length === 0 ? (
      <BodyText color="red600">{t('productPricingCard.missingMaterialCode')}</BodyText>
    ) : missingAcquisitionPrice ? (
      <BodyText color="red600">{t('productPricingCard.missingFinanceAcquisitionPrice')}</BodyText>
    ) : (
      `${acquisitionPrice?.amount} ${currency}`
    );

  const rows: DataTableProps['rows'] = [
    {
      rowType: 'md',
      labelItem: {
        label: t('productPricingCard.financeAcquisitionPrice'),
      },
      dataItem: {
        data: acquisitionPriceLabel,
      },
    },
    {
      rowType: 'md',
      labelItem: {
        label: t('productPricingCard.financeMonthlyPrice'),
      },
      dataItem: {
        data: <BodyText color="gray400">{t('productPricingCard.financeMonthlyPriceInfo')}</BodyText>,
      },
    },
  ];

  return (
    <Card {...cardProps}>
      {priceMasterActive && (
        <>
          <BodyText>{t('productPricingCard.priceFetchedFromPriceMaster')}</BodyText>
          <Gutter />
        </>
      )}
      <Checkbox label={t('productPricingCard.useSameSettingsForAllVariants')} padding={{ top: 'sm' }} checked={true} disabled />
      <Gutter size="lg" />
      <DataTable tableStyle="regular" rows={rows} />
      <Gutter />
      {priceMasterActive ? (
        <Button variant="secondary" onClick={onRefreshProductDataClick}>
          {t('productPricingCard.refreshProductDataButton')}
        </Button>
      ) : (
        <Button variant="secondary" onClick={onEditClick}>
          {t('productPricingCard.editButton')}
        </Button>
      )}
    </Card>
  );
};

export default ProductPricingCard;
